/**
 * Shared workout atoms — TypeChip, ReferenceButton, CuesButton +
 * modal, DifficultyPicker, StatusPill, ExercisePicker.
 *
 * Mirrors the K-OS Health design's screens-health-workouts.jsx but in
 * the codebase's React + CSS-modules style.
 */

import { useState, useMemo, type ReactNode } from 'react';
import { Icon } from '@k-os/ui';
import type { ExerciseDto, ExerciseType, ExerciseBlock } from '../../../api/health';
import styles from './workouts.module.css';

// ─── Type metadata ─────────────────────────────────────────────────────

export const EXERCISE_TYPE_META: Record<
  ExerciseType,
  { name: string; color: string }
> = {
  strength: { name: 'Strength', color: '#7a8a5a' },
  cardio_steady: { name: 'Cardio · steady', color: '#5a7a8a' },
  cardio_intervals: { name: 'Cardio · intervals', color: '#8a5a8a' },
  mobility: { name: 'Mobility', color: '#a8843a' },
  plyometric: { name: 'Plyometric', color: '#b8714a' },
  hold: { name: 'Isometric hold', color: '#7a7363' },
};

/**
 * Resolve a type string to its render metadata, with a safe fallback.
 *
 * The workout-session `type` column is a free-form `text` field — phase
 * materialization, manual API writes, or future loaders may produce
 * values outside the six canonical keys above (`rest`, custom names from
 * the phase editor's "Custom name…" option, etc.). Without a guard, the
 * old `EXERCISE_TYPE_META[type].color` access throws "Cannot read
 * properties of undefined (reading 'color')" and crashes the whole
 * tracker render.
 *
 * This helper returns the canonical entry when possible; otherwise it
 * fabricates a neutral chip using the raw string as the label so the
 * UI still informs the user what they have.
 */
export function metaFor(type: string | null | undefined): { name: string; color: string } {
  if (type && type in EXERCISE_TYPE_META) {
    return EXERCISE_TYPE_META[type as ExerciseType];
  }
  return { name: type ?? 'Workout', color: 'var(--ink-3)' };
}

export const EXERCISE_BLOCK_META: Record<
  ExerciseBlock,
  { name: string; desc: string }
> = {
  warmup: { name: 'Warm-up', desc: 'Prep body and nervous system' },
  main: { name: 'Main', desc: 'Primary lifts or efforts' },
  accessory: { name: 'Accessory', desc: 'Supporting work for stabilizers' },
  cooldown: { name: 'Cool-down', desc: 'Bring heart rate down' },
};

export const DIFFICULTY_SCALE = [
  { v: 1, label: 'Easy', hint: 'Felt like nothing', color: '#5a7a4a' },
  { v: 2, label: 'Light', hint: 'Could do many more', color: '#7a8a5a' },
  { v: 3, label: 'Moderate', hint: 'Working but in control', color: '#a8843a' },
  { v: 4, label: 'Hard', hint: '2-3 reps in reserve', color: '#b8714a' },
  { v: 5, label: 'Max', hint: "Couldn't have done more", color: '#b85544' },
];

// ─── TypeChip ──────────────────────────────────────────────────────────

export function TypeChip({ type }: { type: ExerciseType | string }) {
  // Accept any string; metaFor() falls back to a neutral chip if `type`
  // isn't one of the six canonical keys. This stops the tracker from
  // crashing on sessions with custom or future type values.
  const meta = metaFor(type);
  return (
    <span className={styles.typeChip}>
      <span className={styles.typeChipDot} style={{ background: meta.color }} />
      {meta.name}
    </span>
  );
}

// ─── StatusPill ────────────────────────────────────────────────────────

export function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className={`${styles.statusPill} ${styles.statusPlanned}`}>Planned</span>;
  const lc = status.toLowerCase();
  if (lc === 'completed' || lc === 'done')
    return <span className={`${styles.statusPill} ${styles.statusDone}`}>Completed</span>;
  if (lc === 'skipped')
    return <span className={`${styles.statusPill} ${styles.statusSkipped}`}>Skipped</span>;
  if (lc === 'in_progress' || lc === 'in-progress')
    return <span className={`${styles.statusPill} ${styles.statusInProgress}`}>In progress</span>;
  if (lc === 'partial')
    return <span className={`${styles.statusPill} ${styles.statusPartial}`}>Partial</span>;
  return <span className={`${styles.statusPill} ${styles.statusPlanned}`}>Planned</span>;
}

// ─── Reference button — auto-detects video/pdf/image/page ──────────────

export function refKind(url: string | null | undefined): 'video' | 'pdf' | 'image' | 'page' | null {
  if (!url) return null;
  const u = url.toLowerCase();
  if (/\.(mp4|mov|webm)(\?|$)/.test(u)) return 'video';
  if (/youtube\.com|youtu\.be|vimeo\.com|loom\.com/.test(u)) return 'video';
  if (/\.pdf(\?|$)/.test(u)) return 'pdf';
  if (/\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/.test(u)) return 'image';
  return 'page';
}

const KIND_LABEL = { video: 'Watch', pdf: 'Open PDF', image: 'View image', page: 'Open link' };

export function ReferenceButton({ url, label }: { url: string | null; label?: string }) {
  if (!url) return null;
  const kind = refKind(url)!;
  return (
    <a
      className={styles.refBtn}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`${KIND_LABEL[kind]} — ${url}`}
    >
      <Icon name="sparkles" size={11} />
      <span>{label ?? KIND_LABEL[kind]}</span>
    </a>
  );
}

// ─── Cues modal + trigger button ───────────────────────────────────────

export function CuesButton({ name, cues }: { name: string; cues: string[] }) {
  const [open, setOpen] = useState(false);
  if (!cues || cues.length === 0) return null;
  return (
    <>
      <button
        type="button"
        className={styles.refBtn}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={`${cues.length} cues for ${name}`}
      >
        <Icon name="bell" size={11} />
        <span>Cues</span>
        <span className={styles.refBtnCount}>{cues.length}</span>
      </button>
      {open && (
        <Modal onClose={() => setOpen(false)}>
          <div className={styles.modalHead}>
            <div className={styles.modalEyebrow}>Cues</div>
            <div className={styles.modalTitle}>{name}</div>
          </div>
          <div className={styles.modalBody}>
            <ol className={styles.cuesList}>
              {cues.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ol>
          </div>
          <div className={styles.modalFoot}>
            <span style={{ color: 'var(--ink-4)', fontSize: 13 }}>
              {cues.length} coaching points
            </span>
            <button
              type="button"
              className="kos-btn kos-btn-primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => setOpen(false)}
            >
              Got it
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Difficulty picker (1-5) ───────────────────────────────────────────

export function DifficultyPicker({
  value,
  onPick,
  compact = false,
}: {
  value: number | null;
  onPick: (v: number) => void;
  compact?: boolean;
}) {
  return (
    <div className={styles.diffPicker} role="radiogroup">
      {DIFFICULTY_SCALE.map((d) => (
        <button
          key={d.v}
          type="button"
          className={styles.diffBtn}
          onClick={(e) => {
            e.stopPropagation();
            onPick(d.v);
          }}
          title={`${d.v} · ${d.label} — ${d.hint}`}
          style={
            value === d.v
              ? { background: d.color, color: 'white', borderColor: d.color }
              : {}
          }
        >
          <span className={styles.diffNum}>{d.v}</span>
          {!compact && <span className={styles.diffLabel}>{d.label}</span>}
        </button>
      ))}
    </div>
  );
}

export function DifficultyDot({ value }: { value: number }) {
  const meta = DIFFICULTY_SCALE.find((d) => d.v === value);
  return (
    <span className={styles.diffDot} style={{ background: meta?.color ?? '#888' }}>
      {value}
    </span>
  );
}

// ─── Modal wrapper ─────────────────────────────────────────────────────

export function Modal({
  children,
  onClose,
  width,
}: {
  children: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        className={styles.modal}
        style={width ? { maxWidth: width } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Exercise picker ───────────────────────────────────────────────────

export function ExercisePicker({
  exercises,
  mode,
  defaultBlock,
  onPick,
  onClose,
  onCreateNew,
}: {
  exercises: ExerciseDto[];
  mode: 'add' | 'swap';
  defaultBlock?: ExerciseBlock | undefined;
  onPick: (ex: ExerciseDto) => void;
  onClose: () => void;
  onCreateNew?: (() => void) | undefined;
}) {
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<ExerciseType | 'all'>('all');

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return exercises.filter((e) => {
      if (typeFilter !== 'all' && e.type !== typeFilter) return false;
      if (!lower) return true;
      return (
        e.name.toLowerCase().includes(lower) ||
        e.muscles.some((m) => m.toLowerCase().includes(lower))
      );
    });
  }, [exercises, q, typeFilter]);

  return (
    <Modal onClose={onClose} width={620}>
      <div className={styles.modalHead}>
        <div className={styles.modalEyebrow}>
          {mode === 'swap'
            ? 'Swap exercise'
            : `Add to ${defaultBlock ? EXERCISE_BLOCK_META[defaultBlock].name : 'workout'}`}
        </div>
        <input
          autoFocus
          className={styles.pickerSearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search exercises or muscles…"
        />
      </div>
      <div className={styles.pickerFilters}>
        <button
          type="button"
          className={`${styles.filterChip} ${typeFilter === 'all' ? styles.filterChipActive : ''}`}
          onClick={() => setTypeFilter('all')}
        >
          All
        </button>
        {(Object.keys(EXERCISE_TYPE_META) as ExerciseType[]).map((t) => {
          const m = metaFor(t);
          return (
            <button
              key={t}
              type="button"
              className={`${styles.filterChip} ${typeFilter === t ? styles.filterChipActive : ''}`}
              onClick={() => setTypeFilter(t)}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 2,
                  background: m.color,
                  marginRight: 4,
                }}
              />
              {m.name}
            </button>
          );
        })}
      </div>
      <div className={styles.modalBody}>
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            No matches.
          </div>
        ) : (
          filtered.map((e) => {
            const m = metaFor(e.type);
            return (
            <div key={e.id} className={styles.pickerRow} onClick={() => onPick(e)}>
              <span
                className={styles.pickerRowDot}
                style={{ background: m.color }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={styles.pickerRowName}>{e.name}</div>
                <div className={styles.pickerRowMeta}>
                  {m.name}
                  {e.muscles.length > 0 ? ` · ${e.muscles.join(' · ')}` : ''}
                  {e.prescription ? ` · ${e.prescription}` : ''}
                </div>
              </div>
              <Icon name="arrowRight" size={12} />
            </div>
            );
          })
        )}
      </div>
      <div className={styles.modalFoot}>
        <button type="button" className="kos-btn" onClick={onClose}>
          Cancel
        </button>
        {onCreateNew && (
          <button
            type="button"
            className="kos-btn"
            style={{ marginLeft: 'auto' }}
            onClick={onCreateNew}
          >
            <Icon name="plus" size={11} /> Create new
          </button>
        )}
      </div>
    </Modal>
  );
}

// ─── Skip-reason prompt ────────────────────────────────────────────────

export const SKIP_REASONS = [
  'Equipment unavailable',
  'Not enough time',
  'Low energy / readiness',
  'Pain / injury risk',
  'Other',
] as const;

export function SkipReasonModal({
  exerciseName,
  onConfirm,
  onClose,
}: {
  exerciseName: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<string>(SKIP_REASONS[0]);
  return (
    <Modal onClose={onClose} width={440}>
      <div className={styles.modalHead}>
        <div className={styles.modalEyebrow}>Skip exercise</div>
        <div className={styles.modalTitle}>{exerciseName}</div>
      </div>
      <div className={styles.modalBody}>
        <div className={styles.modalEyebrow}>Why?</div>
        <div className={styles.reasonChips} style={{ marginTop: 8 }}>
          {SKIP_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              className={`${styles.filterChip} ${reason === r ? styles.filterChipActive : ''}`}
              onClick={() => setReason(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.modalFoot}>
        <button type="button" className="kos-btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => onConfirm(reason)}
        >
          Skip exercise
        </button>
      </div>
    </Modal>
  );
}
